import type {BusinessCountryFlags} from '@/types';

/**
 * The switches on a business country, phrased as they are stored.
 *
 * In its own module rather than beside the modal that edits them: a file that
 * exports both a component and a constant loses its Fast Refresh boundary
 * (`react-refresh/only-export-components`), and the modal, the read-only
 * Onboarding tab and the list row all render these.
 *
 * `isDeactivated` is the odd one out and stays inverted rather than being
 * relabelled "active": that is the sense the server filters on, and showing
 * the stored sense avoids a second inversion in the reader's head at exactly
 * the moment they are closing a corridor.
 *
 * `hasPoolAccount` is deliberately absent: the server synthesizes it per
 * request from the pool-account collection and never stores it, so a toggle
 * here would be a switch that does nothing.
 */
export type BusinessSurface = 'business' | 'api';

export type BusinessCountryCorridorFlagKey =
  'isDeactivated' | 'canDeposit' | 'canWithdraw' | 'canSwap';

/**
 * The two surfaces a corridor can be open or closed on, matching the
 * `currency.business` / `currency.api` subtrees the processor selector already
 * reads. Hosted checkout runs on the merchant-API path, which is why the API
 * label says so: closing the API closes checkout with it.
 */
export const BUSINESS_SURFACES: Array<{key: BusinessSurface; label: string}> = [
  {key: 'business', label: 'Dashboard'},
  {key: 'api', label: 'API & Checkout'},
];

/** Held once per surface. */
export const BUSINESS_COUNTRY_CORRIDOR_FLAGS: Array<{
  key: BusinessCountryCorridorFlagKey;
  label: string;
  hint: string;
}> = [
  {
    key: 'isDeactivated',
    label: 'Deactivated',
    hint: 'While this is on, the country is hidden from that surface entirely and takes no new transactions on it, except SWIFT and crypto payouts, which are not tied to a country.',
  },
  {
    key: 'canDeposit',
    label: 'Deposits allowed',
    hint: 'Whether a business can fund a wallet from this country.',
  },
  {
    key: 'canWithdraw',
    label: 'Withdrawals allowed',
    hint: 'Whether a business can pay out into this country.',
  },
  {
    key: 'canSwap',
    label: 'Swaps allowed',
    hint: 'Whether a business registered in this country can swap. Keyed on the business own country, not a destination.',
  },
];

/**
 * Not a corridor switch, so not per surface: onboarding happens on the
 * dashboard and there is no signing up through the API.
 */
export const BUSINESS_COUNTRY_ACCOUNT_FLAGS: Array<{
  key: 'canSignup';
  label: string;
  hint: string;
}> = [
  {
    key: 'canSignup',
    label: 'Signup allowed',
    hint: 'Whether a business can onboard from this country.',
  },
];

/**
 * What the server will act on for one surface.
 *
 * Mirrors `resolveSurfaceFlags` on the server. The flat-key fallback both
 * carried during the migration is gone: the switches are stored per surface
 * and nothing else, and an absent capability means the surface has no opinion.
 */
export const resolveSurfaceFlag = (
  flags: BusinessCountryFlags | undefined,
  surface: BusinessSurface,
  key: BusinessCountryCorridorFlagKey,
): boolean | undefined => flags?.[surface]?.[key];

/**
 * What the server acts on when a surface carries no value for a switch. Only an
 * explicit value closes anything (`assertBusinessCorridorIsLive`), so an unset
 * capability is allowed and an unset `isDeactivated` is live.
 */
export const unsetCorridorFlagValue = (
  key: BusinessCountryCorridorFlagKey,
): boolean => key !== 'isDeactivated';
